import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { SnackbarAlertComponent, SnackbarAlertComponentModule } from '@shared/ui';
import { AuthService } from '@telespot/web-core';
import { UserColumn } from '../../user/list/user-list.component';
import { PageChangeEvent, PaginatedSearchHeaderComponentModule } from '@shared/ui';
import { AffiliatedUserWithMembership, WorkspaceMembersFacade } from './workspace-members.facade';
import { workspaceMembersFacadeProviders } from './workspace-members.facade.providers';
import { WorkspaceMemberData } from '../../../stores/user/workspace-members.state';
import { UserListComponent } from '../../user/list/user-list.component';

@Component({
    selector: 'ts-workspace-members',
    templateUrl: './workspace-members.component.html',
    styleUrls: ['./workspace-members.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        PaginatedSearchHeaderComponentModule,
        UserListComponent,
        SnackbarAlertComponentModule,
    ],
    providers: [...workspaceMembersFacadeProviders]
})
export class WorkspaceMembersComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Columns to display in both lists
  public readonly displayColumns: UserColumn[] = [UserColumn.USERNAME, UserColumn.MEMBER];

  // Only managers/admin manage membership (add/remove); everyone else may only
  // VIEW the member list, so the membership toggle column is dropped and the
  // "available users" (add) list is hidden.
  public get canManageMembers(): boolean {
    return this.authService.hasRoles(['admin', 'analystmanager', 'technicianmanager']);
  }

  public get memberColumns(): UserColumn[] {
    return this.canManageMembers ? this.displayColumns : [UserColumn.USERNAME];
  }

  constructor(
    public readonly facade: WorkspaceMembersFacade,
    private readonly route: ActivatedRoute,
    private readonly snackBar: MatSnackBar,
    private readonly authService: AuthService
  ) {}

  // Organization users (left list)
  public get organizationUsers$() {
    return this.facade.organizationUsers$;
  }

  public get organizationUsersLoading$() {
    return this.facade.organizationUsersLoading$;
  }

  public get hasMoreOrganizationUsers$() {
    return this.facade.hasMoreOrganizationUsers$;
  }

  // Workspace members (right list)
  public get workspaceMembers$() {
    return this.facade.workspaceMembers$;
  }

  public get membersLoading$() {
    return this.facade.membersLoading$;
  }

  public get hasMoreMembers$() {
    return this.facade.hasMoreMembers$;
  }

  ngOnInit(): void {
    // Get workspace ID from route and initialize facade
    this.route.parent?.paramMap
      .pipe(
        map((p) => p.get('workspaceId')),
        filter((p): p is string => !!p),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((workspaceId) => {
        this.facade.initialize({ workspaceId });
      });
  }

  private showOperationError(error: string): void {
    this.snackBar.openFromComponent(SnackbarAlertComponent, {
      duration: 5000,
      data: { title: error, icon: 'ri-error-warning-line' },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Organization users search (left list)
  onOrganizationSearchTermChange(term: string): void {
    this.facade.setOrganizationUsersSearchTerm(term);
  }

  // Workspace members search (right list)
  onMembersSearchTermChange(term: string): void {
    this.facade.setMembersSearchTerm(term);
  }

  // Organization users pagination
  handleOrganizationUserPageEvent(event: PageChangeEvent<AffiliatedUserWithMembership>): void {
    this.facade.loadMoreOrganizationUsers(event.nextToken);
  }

  // Workspace members pagination
  handleMemberPageEvent(event: PageChangeEvent<WorkspaceMemberData>): void {
    this.facade.loadMoreMembers(event.nextToken);
  }

  onOrganizationUserToggle(user: AffiliatedUserWithMembership): void {
    if (user.isMemberOfWorkspace) {
      this.facade.removeMember(user);
    } else {
      this.facade.addMember(user);
    }

    // Listen for errors
    this.facade.membershipOperationError$
      .pipe(
        filter((error): error is string => !!error),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => this.showOperationError(error));
  }

  onMemberToggle(user: AffiliatedUserWithMembership): void {
    this.facade.removeMember(user);

    // Listen for errors
    this.facade.membershipOperationError$
      .pipe(
        filter((error): error is string => !!error),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => this.showOperationError(error));
  }
}
